import React, {useCallback, useRef} from 'react';
import {ChevronLeft, ChevronRight, HelpOutline} from '@mui/icons-material';
import {connect} from 'react-redux';
import {Box, Drawer, useMediaQuery, useTheme} from '@mui/material';
import {sidebarActions} from '../../store/actions/sidebarAction';
import {MainListItems, SidebarNavigationItem} from './ListItems';
import {sidebarCollapsedWidth, sidebarWidth} from './navigation';
import Logo from '../utils/Logo';

export function Sidebar({sidebar, auth, handleDrawerClose, handleDrawerOpen, handleIsLogsOpen}) {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const navigation = useRef(null);
    const expanded = !isDesktop || sidebar.isOpen;
    const width = expanded ? sidebarWidth : sidebarCollapsedWidth;
    const closeOnNavigate = () => {if (!isDesktop) handleDrawerClose();};
    const scrollToActive = useCallback(() => {
        const active = navigation.current?.querySelector('[aria-current="page"]');
        const settings = active?.closest('#sidebar-settings');
        (settings || active)?.scrollIntoView?.({block: settings ? 'end' : 'nearest'});
    }, []);
    const toggle = () => {
        if (!isDesktop || sidebar.isOpen) handleDrawerClose();
        else handleDrawerOpen();
    };
    const toggleLabel = !isDesktop ? 'Close sidebar' : expanded ? 'Collapse sidebar' : 'Expand sidebar';
    const ToggleIcon = expanded ? ChevronLeft : ChevronRight;
    const content = <>
        <Box sx={{height: 50, flexShrink: 0, px: expanded ? 2.5 : 0, display: 'flex', flexDirection: 'column',
            alignItems: expanded ? 'flex-start' : 'center', justifyContent: 'center', gap: 0.75}}>
            <Logo width={expanded ? 64 : 38}/>
        </Box>
        <Box component="nav" aria-label="Main navigation" ref={navigation} sx={{flex: 1, minHeight: 0, overflow: 'hidden', pt: 0.75, pb: 1,
            scrollbarWidth: 'thin', scrollbarColor: `${theme.palette.divider} transparent`}}>
            <MainListItems auth={auth} sidebar={sidebar} expanded={expanded} visible={isDesktop || sidebar.isOpenMobile} onNavigate={closeOnNavigate}
                onExpand={handleDrawerOpen} handleIsLogsOpen={handleIsLogsOpen} onSectionEntered={scrollToActive}/>
        </Box>
        <Box sx={{borderTop: 1, borderColor: 'divider', flexShrink: 0, pt: 1, pb: 'calc(8px + env(safe-area-inset-bottom))'}}>
            <SidebarNavigationItem item={{label: 'Documentation', icon: HelpOutline}} expanded={expanded}
                component="a" href="https://skro.eu/docs" target="_blank" rel="noopener noreferrer" onClick={closeOnNavigate}/>
            <SidebarNavigationItem item={{label: toggleLabel, icon: ToggleIcon}} expanded={false} onClick={toggle}
                aria-expanded={isDesktop ? sidebar.isOpen : sidebar.isOpenMobile} aria-controls="app-sidebar"/>
        </Box>
    </>;
    return (
        <Box sx={{flexShrink: 0}}>
            <Drawer variant={isDesktop ? 'permanent' : 'temporary'} open={isDesktop ? true : sidebar.isOpenMobile}
                onClose={handleDrawerClose} ModalProps={{keepMounted: true}}
                slotProps={{paper: {id: 'app-sidebar', component: 'aside', 'aria-label': 'Sidebar'}, transition: {onEntered: scrollToActive}}}
                sx={{width: isDesktop ? width : 0, flexShrink: 0,
                    transition: theme.transitions.create('width', {duration: theme.transitions.duration.shorter}),
                    '& .MuiDrawer-paper': {width, maxWidth: 'calc(100vw - 48px)', height: 'var(--app-height, 100dvh)',
                        boxSizing: 'border-box', bgcolor: 'background.darkBlue', borderRight: 0, overflow: 'hidden',
                        display: 'flex', flexDirection: 'column',
                        transition: theme.transitions.create('width', {duration: theme.transitions.duration.shorter})}}}>
                {content}
            </Drawer>
        </Box>
    );
}

const mapStateToProps = state => ({sidebar: state.sidebar, auth: state.auth});
const actions = {
    handleDrawerClose: sidebarActions.handleDrawerClose,
    handleDrawerOpen: sidebarActions.handleDrawerOpen,
    handleIsLogsOpen: sidebarActions.handleIsLogsOpen
};
export default connect(mapStateToProps, actions)(Sidebar);
